import toast from 'react-hot-toast';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { history } from '../../shared/routing/browserHistory';
import { sendErrorReport } from '../../shared/utilities/sendErrorReport';
import { updateContractInCache } from '../dossier/cache-utils';

import * as actions from './actions';
import * as api from './api';
import { getAllValidProofOfDocuments } from './selectors';
import {
    serializeContractForAPI,
    serializeInstalmentForAPI,
    serializePaymentScheduleForAPI,
    serializeQuotePricingForAPI,
} from './serializers';
import {
    Contract,
    ContractEditStep,
    FetchContractRequestAction,
    FetchLegalInformationRequestAction,
    PostContractRequestAction,
    SerializedLegalInformation,
    FetchQuotePricingRequestAction,
    QuotePricing,
    PostProofOfDocumentsRequestAction,
    ProofDocument,
    DeleteProofOfDocumentsRequestAction,
    PostChangeQuotePricesRequestAction,
    PostPaymentScheduleRequestAction,
    PutPaymentScheduleRequestAction,
    PostInstalmentRequestAction,
    ExistingPaymentSchedule,
    PutInstalmentRequestAction,
    DeleteInstalmentRequestAction,
} from './types';

function* getContractFlow({ contractId }: FetchContractRequestAction): SagaIterator {
    try {
        const apiResponse: Contract | null = yield call(api.fetchContract, contractId);

        updateContractInCache(contractId, apiResponse);
        yield put(actions.getContractSuccess(apiResponse, contractId));
    } catch (error) {
        yield put(actions.getContractFailure((error as Error).message, contractId));
        sendErrorReport(error as Error);
    }
}

function* createOrUpdateContractFlow({
    contractId,
    travelInformation,
    travelers,
}: PostContractRequestAction): SagaIterator {
    try {
        const contractBody = serializeContractForAPI(contractId, travelInformation, travelers);

        const apiResponse: Contract = yield call(api.postContract, contractId, contractBody);
        yield put(actions.postContractSuccess(apiResponse, contractId));
        toast.success('contract.notifications.updatedSuccess');
        yield put(actions.setContractEditStep(ContractEditStep.PRICING));
    } catch (error) {
        yield put(actions.postContractFailure((error as Error).message, contractId));
        toast.error('contract.notifications.updatedFailure');
        sendErrorReport(error as Error);
    }
}

function* getLegalInformationFlow({
    contractId,
}: FetchLegalInformationRequestAction): SagaIterator {
    try {
        const apiResponse: SerializedLegalInformation[] = yield call(
            api.fetchLegalInformation,
            contractId,
        );
        yield put(actions.getLegalInformationSuccess(apiResponse, contractId));
    } catch (error) {
        yield put(actions.getLegalInformationFailure((error as Error).message, contractId));
        sendErrorReport(error as Error);
    }
}

function* getQuotePricingFlow({ contractId }: FetchQuotePricingRequestAction): SagaIterator {
    try {
        const apiResponse: QuotePricing = yield call(api.fetchQuotePricing, contractId);
        yield put(actions.getQuotePricingSuccess(apiResponse, contractId));
    } catch (error) {
        yield put(actions.getQuotePricingFailure((error as Error).message, contractId));
        sendErrorReport(error as Error);
    }
}

function* postProofOfDocumentsFlow({
    contractId,
    file,
}: PostProofOfDocumentsRequestAction): SagaIterator {
    try {
        const apiResponse: ProofDocument = yield call(api.uploadProofOfDocuments, contractId, file);
        yield put(actions.postProofOfDocumentsSuccess(apiResponse, file.name));
        toast.success('contract.pricing.notifications.uploadedProofOfDocumentsSuccess');
    } catch (error) {
        yield put(actions.postProofOfDocumentsFailure((error as Error).message, file));
        toast.error('global.generic_error');

        sendErrorReport(error as Error);
    }
}

function* deleteProofOfDocumentsFlow({
    fileName,
}: DeleteProofOfDocumentsRequestAction): SagaIterator {
    try {
        const apiResponse: ProofDocument = yield call(api.deleteProofOfDocuments, fileName);
        yield put(actions.deleteProofOfDocumentsSuccess(apiResponse, fileName));
        toast.success('contract.pricing.notifications.deletedProofOfDocumentsSuccess');
    } catch (error) {
        yield put(actions.deleteProofOfDocumentsFailure((error as Error).message));
        toast.error('global.generic_error');
        sendErrorReport(error as Error);
    }
}

function* postChangeQuotePricesFlow({
    contractId,
    quotePrices,
}: PostChangeQuotePricesRequestAction): SagaIterator {
    try {
        const proofOfDocuments: ProofDocument[] = yield select(getAllValidProofOfDocuments);
        const quotePricingBody = serializeQuotePricingForAPI(
            contractId,
            quotePrices,
            proofOfDocuments,
        );

        const apiResponse: QuotePricing = yield call(api.postChangeQuotePrices, quotePricingBody);

        yield put(actions.postChangeQuotePricesSuccess(apiResponse));
        toast.success('contract.pricing.notifications.changeQuotePricesSuccess');
        yield put(actions.setContractEditStep(ContractEditStep.PAYMENT_SCHEDULE));
    } catch (error) {
        yield put(actions.postChangeQuotePricesFailure((error as Error).message));
        toast.error('global.generic_error');
        sendErrorReport(error as Error);
    }
}

function* postPaymentScheduleFlow({
    contractId,
    paymentSchedule,
    onSuccess,
}: PostPaymentScheduleRequestAction): SagaIterator {
    try {
        const paymentScheduleBody = serializePaymentScheduleForAPI(paymentSchedule);
        const apiResponse: ExistingPaymentSchedule = yield call(
            api.postPaymentSchedule,
            contractId,
            paymentScheduleBody,
        );
        yield put(actions.postPaymentScheduleSuccess(apiResponse));
        toast.success('contract.payment_schedule.notifications.createPaymentScheduleSuccess');

        // DossierRevamp: when editing contract in SidePanel (compact view),
        // onSuccess callback is provided to refresh/close panel instead of navigating
        if (onSuccess) {
            onSuccess();
        } else {
            history.push('./');
        }
        // yield put(push(`./`));
    } catch (error) {
        yield put(actions.postPaymentScheduleFailure((error as Error).message));
        toast.error('global.generic_error');
        sendErrorReport(error as Error);
    }
}

function* putPaymentScheduleFlow({
    contractId,
    paymentSchedule,
    onSuccess,
}: PutPaymentScheduleRequestAction): SagaIterator {
    try {
        const paymentScheduleBody = serializePaymentScheduleForAPI(paymentSchedule);

        const apiResponse: ExistingPaymentSchedule = yield call(
            api.putPaymentSchedule,
            contractId,
            paymentScheduleBody,
        );
        yield put(actions.putPaymentScheduleSuccess(apiResponse));
        toast.success('contract.payment_schedule.notifications.updatePaymentScheduleSuccess');

        // DossierRevamp: when editing contract in SidePanel (compact view),
        // onSuccess callback is provided to refresh/close panel instead of navigating
        if (onSuccess) {
            onSuccess();
        } else {
            history.push('./');
        }
        // yield put(push(`./`));
    } catch (error) {
        yield put(actions.putPaymentScheduleFailure((error as Error).message));
        toast.error('global.generic_error');
        sendErrorReport(error as Error);
    }
}

function* postInstalmentFlow({
    contractId,
    instalment,
}: PostInstalmentRequestAction): SagaIterator {
    try {
        const instalmentBody = serializeInstalmentForAPI(instalment);
        const apiResponse: ExistingPaymentSchedule = yield call(
            api.postInstalment,
            contractId,
            instalmentBody,
        );
        yield put(actions.postInstalmentSuccess(apiResponse));
        toast.success('contract.payment_schedule.notifications.addInstalmentSuccess');
    } catch (error) {
        yield put(actions.postInstalmentFailure((error as Error).message));
        toast.error('global.generic_error');
        sendErrorReport(error as Error);
    }
}

function* putInstalmentFlow({ contractId, instalment }: PutInstalmentRequestAction): SagaIterator {
    try {
        const instalmentBody = serializeInstalmentForAPI(instalment);
        const apiResponse: ExistingPaymentSchedule = yield call(
            api.putInstalment,
            contractId,
            instalment.id,
            instalmentBody,
        );
        yield put(actions.putInstalmentSuccess(apiResponse));
        toast.success('contract.payment_schedule.notifications.updateInstalmentSuccess');
    } catch (error) {
        yield put(actions.putInstalmentFailure((error as Error).message));
        toast.error('global.generic_error');
        sendErrorReport(error as Error);
    }
}

function* deleteInstalmentFlow({
    contractId,
    instalmentId,
}: DeleteInstalmentRequestAction): SagaIterator {
    try {
        const apiResponse: ExistingPaymentSchedule = yield call(
            api.deleteInstalment,
            contractId,
            instalmentId,
        );
        yield put(actions.deleteInstalmentSuccess(apiResponse));
        toast.success('contract.payment_schedule.notifications.deleteInstalmentSuccess');
    } catch (error) {
        yield put(actions.deleteInstalmentFailure((error as Error).message));
        toast.error('global.generic_error');
        sendErrorReport(error as Error);
    }
}

export function* getContractWatcher(): SagaIterator {
    yield takeLatest(actions.fetchContractRequestAction, getContractFlow);
    yield takeLatest(actions.postContractRequestAction, createOrUpdateContractFlow);
    yield takeLatest(actions.fetchLegalInformationRequestAction, getLegalInformationFlow);
    yield takeLatest(actions.fetchQuotePricingRequestAction, getQuotePricingFlow);
    yield takeEvery(actions.postProofOfDocumentsRequestAction, postProofOfDocumentsFlow);
    yield takeEvery(actions.deleteProofOfDocumentRequestAction, deleteProofOfDocumentsFlow);
    yield takeLatest(actions.postChangeQuotePricesRequestAction, postChangeQuotePricesFlow);
    yield takeLatest(actions.postPaymentScheduleRequestAction, postPaymentScheduleFlow);
    yield takeLatest(actions.putPaymentScheduleRequestAction, putPaymentScheduleFlow);
    yield takeLatest(actions.postInstalmentRequestAction, postInstalmentFlow);
    yield takeLatest(actions.putInstalmentRequestAction, putInstalmentFlow);
    yield takeEvery(actions.deleteInstalmentRequestAction, deleteInstalmentFlow);
}
